import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Callout,
  Card,
  DateField,
  EmptyState,
  Modal,
  NumberField,
  PropertyList,
  PropertyListItem,
  SelectField,
  Spinner,
  StatCard,
  StatCardGroup,
  Table,
  Tag,
  TextField,
} from '@humanforce/mango-ui-react';
import type {
  CreateWorkersCompJurisdictionRequestDto,
  RenewWorkersCompJurisdictionRequestDto,
  SaveWicEntryRequestDto,
  UpdateWorkersCompJurisdictionRequestDto,
  WicEntryDto,
  WorkersCompJurisdiction,
  WorkersCompJurisdictionCollectionDto,
  WorkersCompJurisdictionDto,
} from '../organisationApiTypes';
import {
  createWorkersCompJurisdiction,
  getWorkersCompJurisdictions,
  renewWorkersCompJurisdiction,
  saveWicEntry,
  updateWicEntry,
  updateWorkersCompJurisdiction,
} from '../workersCompApi';

interface Props {
  businessRecordId: string;
}

type PolicyMode = 'create' | 'edit' | 'renew';

interface PolicyDraft {
  jurisdiction: WorkersCompJurisdiction | '';
  policyYear: string;
  insurerName: string;
  policyNumber: string;
  currentPolicyPeriodStart: string;
  currentPolicyPeriodEnd: string;
  currentEstimatedAnnualWages: number | null;
  brokerName: string;
  effectiveFrom: string;
}

interface WicDraft {
  wicCode: string;
  wicDescription: string;
  basePremiumRate: number | null;
  claimsExperienceAdjustment: number | null;
  apprenticeReductionFactor: number | null;
}

const JURISDICTIONS: WorkersCompJurisdiction[] = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT', 'Comcare'];
const JURISDICTION_OPTIONS = JURISDICTIONS.map((value) => ({ label: value, value }));
const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

const blankPolicy = (): PolicyDraft => ({
  jurisdiction: '',
  policyYear: '',
  insurerName: '',
  policyNumber: '',
  currentPolicyPeriodStart: '',
  currentPolicyPeriodEnd: '',
  currentEstimatedAnnualWages: null,
  brokerName: '',
  effectiveFrom: '',
});

const blankWic = (): WicDraft => ({
  wicCode: '',
  wicDescription: '',
  basePremiumRate: null,
  claimsExperienceAdjustment: null,
  apprenticeReductionFactor: null,
});

const policyDraft = (item: WorkersCompJurisdictionDto): PolicyDraft => ({
  jurisdiction: item.jurisdiction,
  policyYear: item.policyYear,
  insurerName: item.insurerName,
  policyNumber: item.policyNumber ?? '',
  currentPolicyPeriodStart: item.currentPolicyPeriodStart,
  currentPolicyPeriodEnd: item.currentPolicyPeriodEnd,
  currentEstimatedAnnualWages: item.currentEstimatedAnnualWages,
  brokerName: item.brokerName ?? '',
  effectiveFrom: item.effectiveFrom.slice(0, 10),
});

const wicDraft = (item: WicEntryDto): WicDraft => ({
  wicCode: item.wicCode,
  wicDescription: item.wicDescription,
  basePremiumRate: item.basePremiumRate,
  claimsExperienceAdjustment: item.claimsExperienceAdjustment,
  apprenticeReductionFactor: item.apprenticeReductionFactor,
});

const message = (reason: unknown, fallback: string) => reason instanceof Error ? reason.message : fallback;
const optional = (value: string) => value.trim() || null;
const effective = (value: string) => value ? `${value}T00:00:00Z` : null;

const validatePolicy = (
  draft: PolicyDraft,
  mode: PolicyMode,
  currentEffectiveFrom?: string,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (mode === 'create' && !draft.jurisdiction) errors.jurisdiction = 'Jurisdiction is required.';
  if ((mode === 'create' || mode === 'renew') && !draft.policyYear.trim()) errors.policyYear = 'Policy year is required.';
  else if ((mode === 'create' || mode === 'renew') && draft.policyYear.trim().length > 7) errors.policyYear = 'Policy year must be 7 characters or fewer.';
  if (!draft.insurerName.trim()) errors.insurerName = 'Insurer name is required.';
  if (!draft.currentPolicyPeriodStart) errors.currentPolicyPeriodStart = 'Policy period start is required.';
  if (!draft.currentPolicyPeriodEnd) errors.currentPolicyPeriodEnd = 'Policy period end is required.';
  if (draft.currentPolicyPeriodStart && draft.currentPolicyPeriodEnd && draft.currentPolicyPeriodEnd < draft.currentPolicyPeriodStart)
    errors.currentPolicyPeriodEnd = 'Policy period end cannot be before the start date.';
  if (draft.currentEstimatedAnnualWages == null) errors.currentEstimatedAnnualWages = 'Estimated annual wages are required.';
  if (mode === 'edit' && !draft.effectiveFrom) errors.effectiveFrom = 'Successor effective date is required.';
  else if (mode === 'edit' && currentEffectiveFrom && draft.effectiveFrom <= currentEffectiveFrom.slice(0, 10))
    errors.effectiveFrom = `Successor effective date must be after ${currentEffectiveFrom.slice(0, 10)}.`;
  return errors;
};

const validateWic = (draft: WicDraft): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!draft.wicCode.trim()) errors.wicCode = 'WIC code is required.';
  if (!draft.wicDescription.trim()) errors.wicDescription = 'WIC description is required.';
  if (draft.basePremiumRate == null) errors.basePremiumRate = 'Base premium rate is required.';
  return errors;
};

const policyRequest = (draft: PolicyDraft): UpdateWorkersCompJurisdictionRequestDto => ({
  insurerName: draft.insurerName.trim(),
  policyNumber: optional(draft.policyNumber),
  currentPolicyPeriodStart: draft.currentPolicyPeriodStart,
  currentPolicyPeriodEnd: draft.currentPolicyPeriodEnd,
  currentEstimatedAnnualWages: draft.currentEstimatedAnnualWages ?? 0,
  brokerName: optional(draft.brokerName),
  effectiveFrom: effective(draft.effectiveFrom),
});

const wicRequest = (draft: WicDraft): SaveWicEntryRequestDto => ({
  wicCode: draft.wicCode.trim(),
  wicDescription: draft.wicDescription.trim(),
  basePremiumRate: draft.basePremiumRate ?? 0,
  claimsExperienceAdjustment: draft.claimsExperienceAdjustment,
  apprenticeReductionFactor: draft.apprenticeReductionFactor,
});

export const WorkersCompensationPanel = ({ businessRecordId }: Props) => {
  const [collection, setCollection] = useState<WorkersCompJurisdictionCollectionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [policyMode, setPolicyMode] = useState<PolicyMode | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<WorkersCompJurisdictionDto | null>(null);
  const [policy, setPolicy] = useState<PolicyDraft>(blankPolicy);
  const [policyErrors, setPolicyErrors] = useState<Record<string, string>>({});
  const [initialWic, setInitialWic] = useState<WicDraft>(blankWic);
  const [wicErrors, setWicErrors] = useState<Record<string, string>>({});
  const [selectedWic, setSelectedWic] = useState<WicEntryDto | null>(null);
  const [wic, setWic] = useState<WicDraft>(blankWic);
  const [wicPolicy, setWicPolicy] = useState<WorkersCompJurisdictionDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setCollection(await getWorkersCompJurisdictions(businessRecordId));
    } catch (reason) {
      setLoadError(message(reason, 'Workers compensation policies could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [businessRecordId]);

  useEffect(() => { void load(); }, [load, refresh]);

  const items = collection?.items ?? [];
  const totalWic = useMemo(() => items.reduce((total, item) => total + item.wicEntries.length, 0), [items]);
  const readOnly = collection?.meta.businessRecordInactive ?? false;
  const canAdd = (collection?.meta.actions.canAdd ?? false) && !readOnly;

  const replaceItem = (next: WorkersCompJurisdictionDto) => {
    setCollection((current) => current && ({
      ...current,
      items: current.items.some((item) => item.id === next.id)
        ? current.items.map((item) => item.id === next.id ? next : item)
        : [...current.items, next],
    }));
  };

  const openPolicy = (mode: PolicyMode, item: WorkersCompJurisdictionDto | null = null) => {
    setPolicyMode(mode);
    setSelectedPolicy(item);
    const nextPolicy = item ? policyDraft(item) : blankPolicy();
    setPolicy(mode === 'edit' ? { ...nextPolicy, effectiveFrom: '' } : nextPolicy);
    setInitialWic(blankWic());
    setPolicyErrors({});
    setWicErrors({});
    setSaveError(null);
  };

  const closePolicy = () => {
    if (saving) return;
    setPolicyMode(null);
    setSelectedPolicy(null);
    setSaveError(null);
  };

  const savePolicy = async () => {
    if (!policyMode) return;
    const nextPolicyErrors = validatePolicy(policy, policyMode, selectedPolicy?.effectiveFrom);
    const nextWicErrors = policyMode === 'create' ? validateWic(initialWic) : {};
    setPolicyErrors(nextPolicyErrors);
    setWicErrors(nextWicErrors);
    if (Object.keys(nextPolicyErrors).length || Object.keys(nextWicErrors).length) return;

    setSaving(true);
    setSaveError(null);
    try {
      let result: WorkersCompJurisdictionDto;
      if (policyMode === 'create') {
        result = await createWorkersCompJurisdiction(businessRecordId, {
          ...policyRequest(policy),
          jurisdiction: policy.jurisdiction as WorkersCompJurisdiction,
          policyYear: policy.policyYear.trim(),
          wicEntries: [wicRequest(initialWic)],
        } satisfies CreateWorkersCompJurisdictionRequestDto);
      } else if (policyMode === 'renew' && selectedPolicy) {
        result = await renewWorkersCompJurisdiction(businessRecordId, selectedPolicy.id, {
          ...policyRequest(policy),
          policyYear: policy.policyYear.trim(),
        } satisfies RenewWorkersCompJurisdictionRequestDto);
      } else if (selectedPolicy) {
        result = await updateWorkersCompJurisdiction(businessRecordId, selectedPolicy.id, policyRequest(policy));
      } else {
        return;
      }
      replaceItem(result);
      setPolicyMode(null);
      setSelectedPolicy(null);
      setSaveError(null);
    } catch (reason) {
      setSaveError(message(reason, 'The policy could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const openWic = (item: WorkersCompJurisdictionDto, entry: WicEntryDto | null = null) => {
    setWicPolicy(item);
    setSelectedWic(entry);
    setWic(entry ? wicDraft(entry) : blankWic());
    setWicErrors({});
    setSaveError(null);
  };

  const closeWic = () => {
    if (saving) return;
    setWicPolicy(null);
    setSelectedWic(null);
    setSaveError(null);
  };

  const saveWic = async () => {
    if (!wicPolicy) return;
    const errors = validateWic(wic);
    setWicErrors(errors);
    if (Object.keys(errors).length) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = selectedWic
        ? await updateWicEntry(businessRecordId, wicPolicy.id, selectedWic.id, wicRequest(wic))
        : await saveWicEntry(businessRecordId, wicPolicy.id, wicRequest(wic));
      replaceItem(result);
      setWicPolicy(null);
      setSelectedWic(null);
      setSaveError(null);
    } catch (reason) {
      setSaveError(message(reason, 'The WIC entry could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !collection) {
    return (
      <div aria-busy="true" aria-label="Loading workers compensation policies" className="mgo-cluster mgo-justify-center mgo-border-neutral" style={{ minHeight: '15rem' }}>
        <div style={{ fontSize: 'var(--mgo-size-80)' }}><Spinner /></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <Callout tone="critical" title="Workers compensation policies could not be loaded" action={<Button label="Retry" size="small" variant="secondary" onClick={() => setRefresh((value) => value + 1)} />}>
        {loadError}
      </Callout>
    );
  }

  const wicColumns = (item: WorkersCompJurisdictionDto) => [
    { field: 'wicCode', header: 'WIC code' },
    { field: 'wicDescription', header: 'Description' },
    { field: 'basePremiumRate', header: 'Base premium rate', body: (entry: WicEntryDto) => `${entry.basePremiumRate}%` },
    { field: 'claimsExperienceAdjustment', header: 'Claims adjustment' },
    { field: 'apprenticeReductionFactor', header: 'Apprentice factor' },
    {
      field: 'actions',
      header: 'Actions',
      body: (entry: WicEntryDto) => (
        <Button
          label={`Edit ${entry.wicCode}`}
          variant="tertiary"
          size="small"
          disabled={!item.meta.actions.canEdit || readOnly}
          onClick={() => openWic(item, entry)}
        />
      ),
    },
  ];

  return (
    <div className="mgo-stack mgo-gap-l">
      <Callout
        tone="info"
        title="Workers compensation policy settings"
        action={<Button label="Add jurisdiction" icon="add" disabled={!canAdd} onClick={() => openPolicy('create')} />}
      >
        Maintain insurer, policy, wage estimate, and WIC details for this Business Record. Premium calculation and provision accrual are handled separately.
      </Callout>

      <StatCardGroup>
        <StatCard label="Jurisdictions" value={items.length} icon="location" />
        <StatCard label="WIC entries" value={totalWic} icon="report-table" />
        <StatCard label="Policy years" value={new Set(items.map((item) => item.policyYear)).size} icon="date" />
      </StatCardGroup>

      {(!collection?.meta.actions.canEdit || readOnly) && (
        <Callout tone="neutral" title="Workers compensation policies are read-only">
          {readOnly
            ? "This Business Record is inactive, so policies and WIC entries can't be changed."
            : "Your role doesn't include permission to change workers compensation policies."}
        </Callout>
      )}

      {items.length === 0 ? (
        <Card>
          <div className="mgo-stack mgo-justify-center mgo-align-center mgo-padding-l" style={{ minHeight: '16rem' }}>
            <EmptyState
              variant="no-data"
              title="No workers compensation policies"
              description="Add the first jurisdiction policy and its required WIC entry."
              actions={[<Button key="add" label="Add jurisdiction" icon="add" disabled={!canAdd} onClick={() => openPolicy('create')} />]}
            />
          </div>
        </Card>
      ) : items.map((item) => (
        <Card key={item.id} title={`${item.jurisdiction} policy`}>
          <div className="mgo-stack mgo-gap-l">
            <div className="mgo-split mgo-align-start">
              <div className="mgo-cluster mgo-gap-s">
                <Tag label={`Policy year ${item.policyYear}`} tone="blue" />
                <Tag label={`Est. wages ${currency.format(item.currentEstimatedAnnualWages)}`} tone="grey" />
              </div>
              <div className="mgo-cluster mgo-gap-s">
                <Button label="Edit policy" icon="edit" variant="secondary" size="small" disabled={!item.meta.actions.canEdit || readOnly} onClick={() => openPolicy('edit', item)} />
                <Button label="Renew policy" icon="refresh" variant="secondary" size="small" disabled={!item.meta.actions.canRenew || readOnly} onClick={() => openPolicy('renew', item)} />
              </div>
            </div>

            <PropertyList>
              <PropertyListItem label="Insurer">{item.insurerName}</PropertyListItem>
              <PropertyListItem label="Policy number">{item.policyNumber || 'Not provided'}</PropertyListItem>
              <PropertyListItem label="Policy period">{item.currentPolicyPeriodStart} to {item.currentPolicyPeriodEnd}</PropertyListItem>
              <PropertyListItem label="Broker">{item.brokerName || 'Not provided'}</PropertyListItem>
            </PropertyList>

            {item.advisories.map((advisory) => <Callout key={advisory.code} tone="info">{advisory.message}</Callout>)}

            <div className="mgo-split">
              <span className="mgo-text-m mgo-weight-strong">WIC entries</span>
              <Button label="Add WIC entry" icon="add" variant="secondary" size="small" disabled={!item.meta.actions.canAddWic || readOnly} onClick={() => openWic(item)} />
            </div>
            <Table value={item.wicEntries} columns={wicColumns(item)} emptyMessage="No WIC entries" scrollable testId={`workers-comp-wic-${item.id}`} />
          </div>
        </Card>
      ))}

      {policyMode && (
        <Modal
          visible
          header={policyMode === 'create' ? 'Add jurisdiction' : policyMode === 'renew' ? `Renew ${selectedPolicy?.jurisdiction} policy` : `Edit ${selectedPolicy?.jurisdiction} policy`}
          onHide={closePolicy}
          footer={(
            <>
              <Button label="Cancel" variant="secondary" disabled={saving} onClick={closePolicy} />
              <Button label={policyMode === 'renew' ? 'Renew policy' : policyMode === 'create' ? 'Add jurisdiction' : 'Save policy'} loading={saving} onClick={() => void savePolicy()} />
            </>
          )}
        >
          <div className="mgo-stack mgo-gap-l">
            {saveError && <Callout tone="critical">{saveError}</Callout>}
            <PolicyFields mode={policyMode} draft={policy} errors={policyErrors} onChange={setPolicy} />
            {policyMode === 'create' && (
              <div className="mgo-stack mgo-gap-s">
                <span className="mgo-text-m mgo-weight-strong">First WIC entry</span>
                <WicFields draft={initialWic} errors={wicErrors} onChange={setInitialWic} />
              </div>
            )}
            {policyMode === 'renew' && selectedPolicy && (
              <div className="mgo-stack mgo-gap-s">
                <Callout tone="info">These WIC entries will be copied to the renewed policy for review.</Callout>
                <Table value={selectedPolicy.wicEntries} columns={wicColumns(selectedPolicy).slice(0, 5)} emptyMessage="No WIC entries to copy" />
              </div>
            )}
          </div>
        </Modal>
      )}

      {wicPolicy && (
        <Modal
          visible
          header={selectedWic ? `Edit ${selectedWic.wicCode}` : `Add WIC entry to ${wicPolicy.jurisdiction}`}
          onHide={closeWic}
          footer={(
            <>
              <Button label="Cancel" variant="secondary" disabled={saving} onClick={closeWic} />
              <Button label={selectedWic ? 'Save WIC entry' : 'Add WIC entry'} loading={saving} onClick={() => void saveWic()} />
            </>
          )}
        >
          <div className="mgo-stack mgo-gap-l">
            {saveError && <Callout tone="critical">{saveError}</Callout>}
            <WicFields draft={wic} errors={wicErrors} onChange={setWic} />
          </div>
        </Modal>
      )}
    </div>
  );
};

const PolicyFields = ({ mode, draft, errors, onChange }: {
  mode: PolicyMode;
  draft: PolicyDraft;
  errors: Record<string, string>;
  onChange: (draft: PolicyDraft) => void;
}) => (
  <div className="mgo-stack mgo-gap-l">
    {(mode === 'create' || mode === 'renew') && (
      <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
        {mode === 'create' && (
          <SelectField
            label="Jurisdiction"
            required
            searchable={false}
            value={draft.jurisdiction || null}
            options={JURISDICTION_OPTIONS}
            status={errors.jurisdiction ? 'critical' : ''}
            statusText={errors.jurisdiction}
            onChange={(value) => onChange({ ...draft, jurisdiction: typeof value === 'string' ? value as WorkersCompJurisdiction : '' })}
          />
        )}
        <TextField
          label="Policy year"
          required
          value={draft.policyYear}
          placeholder="2026-27"
          helpText="For example, 2026-27. Use no more than 7 characters."
          status={errors.policyYear ? 'critical' : undefined}
          statusText={errors.policyYear}
          onChange={(event) => onChange({ ...draft, policyYear: event.target.value })}
        />
      </div>
    )}
    <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
      <TextField label="Insurer name" required value={draft.insurerName} status={errors.insurerName ? 'critical' : undefined} statusText={errors.insurerName} onChange={(event) => onChange({ ...draft, insurerName: event.target.value })} />
      <TextField label="Policy number" value={draft.policyNumber} onChange={(event) => onChange({ ...draft, policyNumber: event.target.value })} />
    </div>
    <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
      <DateField label="Policy period start" required fullWidth dateFormat="ymd" value={draft.currentPolicyPeriodStart} status={errors.currentPolicyPeriodStart ? 'critical' : undefined} statusText={errors.currentPolicyPeriodStart} onChange={(value) => onChange({ ...draft, currentPolicyPeriodStart: value })} />
      <DateField label="Policy period end" required fullWidth dateFormat="ymd" value={draft.currentPolicyPeriodEnd} status={errors.currentPolicyPeriodEnd ? 'critical' : undefined} statusText={errors.currentPolicyPeriodEnd} onChange={(value) => onChange({ ...draft, currentPolicyPeriodEnd: value })} />
    </div>
    <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
      <NumberField label="Estimated annual wages" required min={0} allowNegative={false} precision={2} leadingIcon="dollar-sign" value={draft.currentEstimatedAnnualWages} status={errors.currentEstimatedAnnualWages ? 'critical' : undefined} statusText={errors.currentEstimatedAnnualWages} onChange={(value) => onChange({ ...draft, currentEstimatedAnnualWages: value })} />
      <TextField label="Broker name" value={draft.brokerName} onChange={(event) => onChange({ ...draft, brokerName: event.target.value })} />
    </div>
    <DateField
      label="Effective from"
      required={mode === 'edit'}
      fullWidth
      dateFormat="ymd"
      value={draft.effectiveFrom}
      helpText={mode === 'edit' ? 'Choose when this successor version takes effect.' : undefined}
      status={errors.effectiveFrom ? 'critical' : undefined}
      statusText={errors.effectiveFrom}
      onChange={(value) => onChange({ ...draft, effectiveFrom: value })}
    />
  </div>
);

const WicFields = ({ draft, errors, onChange }: {
  draft: WicDraft;
  errors: Record<string, string>;
  onChange: (draft: WicDraft) => void;
}) => (
  <div className="mgo-stack mgo-gap-l">
    <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
      <TextField label="WIC code" required value={draft.wicCode} status={errors.wicCode ? 'critical' : undefined} statusText={errors.wicCode} onChange={(event) => onChange({ ...draft, wicCode: event.target.value })} />
      <TextField label="WIC description" required value={draft.wicDescription} status={errors.wicDescription ? 'critical' : undefined} statusText={errors.wicDescription} onChange={(event) => onChange({ ...draft, wicDescription: event.target.value })} />
    </div>
    <div role="group" aria-label="WIC rate fields" className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
      <NumberField label="Base premium rate" helpText="Enter a percentage, for example 0.85 for 0.85%." required min={0} allowNegative={false} precision={4} trailingIcon="percent" value={draft.basePremiumRate} status={errors.basePremiumRate ? 'critical' : undefined} statusText={errors.basePremiumRate} onChange={(value) => onChange({ ...draft, basePremiumRate: value })} />
      <NumberField label="Claims experience adjustment" allowNegative={false} precision={4} value={draft.claimsExperienceAdjustment} onChange={(value) => onChange({ ...draft, claimsExperienceAdjustment: value })} />
      <NumberField label="Apprentice reduction factor" allowNegative={false} precision={4} value={draft.apprenticeReductionFactor} onChange={(value) => onChange({ ...draft, apprenticeReductionFactor: value })} />
    </div>
  </div>
);
